import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import BalanceOutlined from '@mui/icons-material/BalanceOutlined';
import CreditScoreOutlined from '@mui/icons-material/CreditScoreOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import MonitorHeartOutlined from '@mui/icons-material/MonitorHeartOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import RuleOutlined from '@mui/icons-material/RuleOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import SpaceDashboardOutlined from '@mui/icons-material/SpaceDashboardOutlined';
import SportsEsportsOutlined from '@mui/icons-material/SportsEsportsOutlined';
import SupportAgentOutlined from '@mui/icons-material/SupportAgentOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import type SvgIcon from '@mui/material/SvgIcon';

import { FeatureScaffoldPage } from '../shared/layout/FeatureScaffoldPage';
import { paths } from './paths';
import type { FeatureRoute, NavGroup, NavGroupId } from './types';

const routes: readonly FeatureRoute[] = [
  feature(
    paths.dashboard,
    'nav.dashboard',
    'overview',
    'REPORT_VIEW',
    'dashboard',
    SpaceDashboardOutlined,
  ),
  feature(
    paths.partners,
    'nav.partners',
    'organization',
    'PARTNER_VIEW',
    'partners',
    HandshakeOutlined,
  ),
  feature(paths.agents, 'nav.agents', 'organization', 'AGENT_VIEW', 'agents', SupportAgentOutlined),
  feature(
    paths.adminUsers,
    'nav.adminUsers',
    'organization',
    'BACKOFFICE_USER_MANAGE',
    'adminUsers',
    AdminPanelSettingsOutlined,
  ),
  feature(paths.members, 'nav.members', 'affiliates', 'MEMBER_VIEW', 'members', GroupsOutlined),
  feature(
    paths.affiliateWallets,
    'nav.affiliateWallets',
    'affiliates',
    'WALLET_VIEW',
    'wallets',
    AccountBalanceWalletOutlined,
  ),
  feature(
    paths.affiliateWithdrawals,
    'nav.withdrawals',
    'affiliates',
    'WITHDRAWAL_VIEW',
    'withdrawals',
    PaymentsOutlined,
  ),
  feature(paths.games, 'nav.games', 'affiliates', 'GAME_VIEW', 'games', SportsEsportsOutlined),
  feature(
    paths.commissionRules,
    'nav.commissionRules',
    'commissions',
    'COMMISSION_RULE_VIEW',
    'commissionRules',
    RuleOutlined,
  ),
  feature(
    paths.commissionLedger,
    'nav.commissionLedger',
    'commissions',
    'COMMISSION_LEDGER_VIEW',
    'commissionLedger',
    ReceiptLongOutlined,
  ),
  feature(
    paths.transactions,
    'nav.transactions',
    'operations',
    'TRANSACTION_VIEW',
    'transactions',
    SwapHorizOutlined,
  ),
  feature(
    paths.transactionFailures,
    'nav.transactionFailures',
    'operations',
    'TRANSACTION_VIEW',
    'transactionFailures',
    ReportProblemOutlined,
  ),
  feature(
    paths.apiOperations,
    'nav.apiOperations',
    'operations',
    'REPORT_VIEW',
    'apiOperations',
    MonitorHeartOutlined,
  ),
  feature(
    paths.reconciliation,
    'nav.reconciliation',
    'operations',
    'RECONCILIATION_MANAGE',
    'reconciliation',
    BalanceOutlined,
  ),
  feature(
    paths.partnerCredit,
    'nav.partnerCredit',
    'operations',
    'INTEGRATION_MANAGE',
    'partnerCredit',
    CreditScoreOutlined,
  ),
  feature(paths.reports, 'nav.reports', 'operations', 'REPORT_VIEW', 'reports', AssessmentOutlined),
  feature(
    paths.reportExports,
    'nav.reportExports',
    'operations',
    'REPORT_EXPORT',
    'reportExports',
    FileDownloadOutlined,
  ),
  feature(
    paths.approvals,
    'nav.approvals',
    'governance',
    'APPROVAL_DECIDE',
    'approvals',
    TaskAltOutlined,
  ),
  feature(paths.audit, 'nav.audit', 'governance', 'AUDIT_VIEW', 'audit', HistoryOutlined),
  feature(
    paths.securityEvents,
    'nav.securityEvents',
    'governance',
    'AUDIT_VIEW',
    'securityEvents',
    ShieldOutlined,
  ),
];

const groupOrder: readonly NavGroupId[] = [
  'overview',
  'organization',
  'affiliates',
  'commissions',
  'operations',
  'governance',
];

export const sitemap: readonly NavGroup[] = groupOrder.map((id) => ({
  id,
  labelKey: `nav.groups.${id}`,
  items: routes.filter((route) => route.group === id),
}));

function feature(
  path: string,
  labelKey: string,
  group: NavGroupId,
  permission: string,
  capability: FeatureRoute['capability'],
  icon: typeof SvgIcon,
): FeatureRoute {
  return {
    path,
    labelKey,
    group,
    permission,
    capability,
    icon,
    element: <FeatureScaffoldPage capability={capability} labelKey={labelKey} />,
  };
}
